import moment from 'moment';
import qs from 'query-string';
import bs58 from 'bs58';
import { cloneDeep } from 'lodash';
import config from 'react-global-configuration';
import { SERVICES_DATA } from '@shared/data/services';
import { ERROR_STATUSES, SUCCESS_STATUSES } from '@shared/constants/payment.constants';
import { formatCurrency } from '@utils/helpers/formatters';
import { applyMomentLocale, getCurrentI18nLanguage } from '@utils/helpers/localeFormatting';
import { parseDateTimeMs } from '@utils/helpers/dateHelpers';

const taskExpiredAfter = config.get('taskExpiredAfter');

export function capitalize(s) {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function trimTaskMetadata(task) {
  const formatedTask = { ...task };

  delete formatedTask.user_id;
  delete formatedTask.customer_email;

  return formatedTask;
}

export function getTags(metadata) {
  if (metadata) {
    const { tags } = metadata;

    if (tags) {
      if (Array.isArray(tags)) {
        return tags;
      } else {
        return tags.split(', ');
      }
    }
  }

  return [];
}

export function getCategories(metadata) {
  if (!metadata) return [];
  const { categories } = metadata;
  if (!categories) return [];
  if (Array.isArray(categories)) return categories;
  return String(categories).split(', ');
}

export function parseQueries(text) {
  return text.split(/\r?\n/).filter((el) => { return !!el; });
}

export function toQueries(value) {
  if (Array.isArray(value)) {
    return arrayToText(value);
  } else {
    return value;
  }
}

export function splitTaskByKey(task, splitInto, key) {
  const results = [];
  const items = cloneDeep(task[key]);
  delete task[key];
  let index = 1;

  for (const chunkItems of chunkArray(items, Math.ceil(items.length / splitInto))) {
    const newTask = cloneDeep(task);
    const partSuffix = ` (part ${index})`;
    const partTitle = newTask.title
      ? `${newTask.title.slice(0, 200 - partSuffix.length).trim()}${partSuffix}`
      : newTask.title;

    results.push({
      ...newTask,
      title: partTitle,
      [key]: chunkItems
    });
    index += 1;
  }

  return results;
}

export function arrayToText(array) {
  return array.join('\n');
}

export function delay(duration) {
  return new Promise(resolve => setTimeout(resolve, duration));
}

export function toArray(value) {
  if (!value) return [];
  return typeof value === 'string' ? [value] : value;
}

export function isValidEmail(email) {
  return /^\w+([.+-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(email);
}

export function getServiceUrl(serviceName) {
  if (SERVICES_DATA[serviceName]) {
    return SERVICES_DATA[serviceName].key;
  } else {
    if (servicesUrls[serviceName]) {
      return servicesUrls[serviceName];
    }
  }
  return serviceName.replaceAll('_service', '').replaceAll('_v2', '').replaceAll('_v3', '').replaceAll('_v4', '').replaceAll('_', '-');
}

export function getBaseServiceName(serviceName) {
  return serviceName.replace(/_v\d+$/, '');
}

export function getService(serviceKey) {
  if (!serviceKey) return null;

  const keyParts = serviceKey.split('@');
  let logicalKey = keyParts[0];
  const explicitVersion = keyParts.length > 1 ? parseInt(keyParts[1], 10) : null;

  logicalKey = logicalKey.replace(/_v\d+$/, '');

  const config = SERVICES_DATA[logicalKey];

  if (!config) {
    console.warn(`Service with key "${logicalKey}" not found.`);
    return {
      logicalKey,
      backendKey: logicalKey,
      isDeprecated: true,
      label: logicalKey,
    };
  }

  const version = explicitVersion || config.version;

  const backendKey = (version && version > 1)
    ? `${logicalKey}_v${version}`
    : logicalKey;

  return {
    ...config,
    logicalKey,
    backendKey,
  };
}

export function updateLocalStorage(key, value) {
  if (value) {
    localStorage.setItem(key, value);
  } else {
    localStorage.removeItem(key);
  }
}

export const servicesUrls = {
  'google_maps_service': 'googleMapsV1',
  'google_maps_service_v2': 'googleMaps',
  'google_maps_service_v3': 'googleMaps',
  'google_maps_and_contacts_service_v2': 'googleMaps',
  'google_maps_reviews_service_v2': 'googleReviewsV2',
  'google_maps_reviews_service_v3': 'googleReviews',
  'google_maps_traffic_service': 'googleTraffic',
  'google_shopping_reviews_service': 'google-shopping-reviews',
  'domains_service': 'emails-scraper',
  'ai_scraper_service': 'universal-scraper',
  'ai_scraper_service_v2': 'universal-scraper',
};

export function replaceAll(str, find, replace) {
  return str.replace(new RegExp(find, 'g'), replace);
}

export function chunkArray(items, itemsInChunk) {
  return items.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / itemsInChunk);
    if (!resultArray[chunkIndex]) resultArray[chunkIndex] = [];
    resultArray[chunkIndex].push(item);
    return resultArray;
  }, []);
}

export function getEstimateDuration(duration, splitInto) {
  applyMomentLocale();

  if (!duration) return '';
  const durationDiff = moment.duration(duration / (splitInto > 1 ? splitInto : 1), 'minutes').humanize({ h: 48 });
  const numberValue = (durationDiff.match(/\d/g) || []).join('');
  return numberValue ? durationDiff.replace(numberValue, `${parseInt(parseInt(numberValue) * 0.7)} - ${parseInt(parseInt(numberValue) * 2.5)}`) : durationDiff;
}

export function formatBalance(balance) {
  return formatCurrency(balance, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function addUrlParams(url, params, ignoreNull = false) {
  const mergedParams = { ...qs.parse(url), ...params };
  if (ignoreNull) {
    // eslint-disable-next-line no-unused-vars
    return qs.stringify(Object.fromEntries(Object.entries(mergedParams).filter(([key, value]) => value !== null)));
  } else {
    return qs.stringify(mergedParams);
  }
}

export function isValidHttpUrl(string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

function isValidIPv4(value) {
  const parts = value.split('.');

  if (parts.length !== 4) return false;

  return parts.every(part => /^\d{1,3}$/.test(part) && Number(part) >= 0 && Number(part) <= 255);
}

export function isValidHostOrIp(value) {
  if (!value) return false;

  const normalizedValue = value.trim();

  if (!normalizedValue || normalizedValue.includes('://') || /[/\s]/.test(normalizedValue)) {
    return false;
  }

  if (isValidIPv4(normalizedValue)) {
    return true;
  }

  if (normalizedValue.length > 253 || normalizedValue.includes('..')) {
    return false;
  }

  const parts = normalizedValue.split('.');

  if (parts.every(part => /^\d+$/.test(part))) {
    return false;
  }

  return parts.every(part => /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/.test(part));
}

export function validateSftpIntegration(values = {}, t = key => key) {
  const normalizedValues = {
    host: values.host?.trim() || '',
    port: `${values.port ?? ''}`.trim()
      .replace(/[\u0660-\u0669]/g, digit => String(digit.charCodeAt(0) - 0x0660))
      .replace(/[\u06f0-\u06f9]/g, digit => String(digit.charCodeAt(0) - 0x06f0)),
    user: values.user?.trim() || '',
    password: values.password?.trim() || '',
    remote_path: values.remote_path?.trim() || '',
  };
  const errors = {};

  if (!normalizedValues.host) {
    errors.host = t('message.sftpHostRequired');
  } else if (!isValidHostOrIp(normalizedValues.host)) {
    errors.host = t('message.sftpHostInvalid');
  }

  if (!normalizedValues.port) {
    errors.port = t('message.sftpPortRequired');
  } else if (!/^\d+$/.test(normalizedValues.port)) {
    errors.port = t('message.sftpPortInteger');
  } else {
    const parsedPort = Number(normalizedValues.port);

    if (parsedPort < 1 || parsedPort > 65535) {
      errors.port = t('message.sftpPortRange');
    }
  }

  if (!normalizedValues.user) {
    errors.user = t('message.sftpUserRequired');
  }

  if (!normalizedValues.password) {
    errors.password = t('message.sftpPasswordRequired');
  }

  if (!normalizedValues.remote_path) {
    errors.remote_path = t('message.sftpRemotePathRequired');
  } else if (normalizedValues.remote_path.includes('\\')) {
    errors.remote_path = t('message.sftpRemotePathForwardSlashes');
  } else if (normalizedValues.remote_path.split('/').includes('..')) {
    errors.remote_path = t('message.sftpRemotePathTraversal');
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    values: {
      ...normalizedValues,
      port: Number(normalizedValues.port),
    }
  };
}

export function getLanguage(defaultLanguage = 'en') {
  return getCurrentI18nLanguage(undefined, defaultLanguage);
}

export function isCredibleUser() {
  return localStorage.getItem('credibleUser');
}

export function setCredibleUser() {
  return localStorage.setItem('credibleUser', '1');
}

export function setPaidUser(isPaidUser) {
  updateLocalStorage('paidUser', JSON.stringify(isPaidUser));
}

export function setGoogleIntegrationExists(value) {
  updateLocalStorage('googleIntegration', JSON.stringify(value));
}

export function isGoogleIntegrationExists() {
  return localStorage.getItem('googleIntegration') === 'true';
}

export function isPaidUser() {
  return localStorage.getItem('paidUser') === 'true';
}

export function isRecentlyUsedNTimes(key, n = 3, timeLimitInMinutes = 15) {
  let storedTimestamps = JSON.parse(localStorage.getItem(key)) || [];

  const currentTime = new Date().getTime();
  const timeLimitInMilliseconds = timeLimitInMinutes * 60 * 1000;
  storedTimestamps = storedTimestamps.filter(timestamp => currentTime - timestamp <= timeLimitInMilliseconds);

  storedTimestamps.push(currentTime);

  storedTimestamps = storedTimestamps.filter(timestamp => currentTime - timestamp <= timeLimitInMilliseconds);

  localStorage.setItem(key, JSON.stringify(storedTimestamps));

  return storedTimestamps.length > n;
}

export function toTitleCase(str) {
  return str.replace(/\w\S*/g, text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
}

export function toCamelCase(postalCode) {
  return postalCode
    .split(' ')
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
}

export function pluralize(text) {
  if (!text) return '';

  const word = text.split('/')[0];

  if (/(s|x|z|ch|sh)$/i.test(word)) {
    return word + 'es';
  }

  if (/[^aeiou]y$/i.test(word)) {
    return word.slice(0, -1) + 'ies';
  }

  if (/f$|fe$/i.test(word)) {
    return word.replace(/fe?$/i, 'ves');
  }

  if (/[^aeiou]o$/i.test(word)) {
    return word + 'es';
  }

  return word + 's';
}

export function displayWords(words, N, emptyText = '') {
  if (!Array.isArray(words) || words.length === 0) return emptyText;

  if (words.length <= N) {
    return words.join(', ');
  } else {
    return `${words.slice(0, N).join(', ')} and ${words.length - N} more`;
  }
}

export function getConfigUrl(serviceName, linkType) {
  const { [linkType]: links, homeUrl } = config.get();

  if (!links || !links[serviceName]) return null;

  const url = links[serviceName];

  return url.startsWith('/') ? `https://${homeUrl}${url}` : url;
}

export const getLearnMoreUrl = (serviceName) => getConfigUrl(serviceName, 'learnMoreLinks');
export const getVideoTutorialUrl = (serviceName) => getConfigUrl(serviceName, 'videoTutorialLinks');
export const getTutorialUrl = (serviceName) => getConfigUrl(serviceName, 'tutorialsLinks');

export function getOtherUrl(key, prefix = `https://${config.get('homeUrl')}`) {
  const link = config.get('otherLinks')?.[key];
  if (!link) return null;
  return link.startsWith('http') ? link : `${prefix}${link}`;
}

export const isErrorStatus = (status) => Boolean(ERROR_STATUSES[status]);
export const isSuccessStatus = (status) => SUCCESS_STATUSES.includes(status);

export function encodeToBase58(text) {
  const textEncoder = new TextEncoder();
  const bytes = textEncoder.encode(text);
  return bs58.encode(bytes);
}

export const createTOSData = (email, version = 'v2.1') => {
  return {
    tos_accepted: true,
    tos_version: version,
    tos_accepted_at: new Date().toISOString(),
    tos_accepted_user_agent: navigator.userAgent,
    email: email,
  };
};

export const isTaskExpired = (createdDate) => {
  const createdDateMs = parseDateTimeMs(createdDate);
  if (createdDateMs == null) return false;

  return Date.now() - createdDateMs > taskExpiredAfter * 60 * 60 * 24 * 1000;
};

export const isEmptySchema = (schema) => {
  return (
    schema &&
      schema.type === 'object' &&
      (!schema.properties || Object.keys(schema.properties).length === 0) &&
      (!schema.required || schema.required.length === 0)
  );
};
